import { EnumUserRoles } from 'entities/Users/model/types'
import { type NavTarget } from 'entities/WebMcp/model/navAreas'

/**
 * Role-based gating of navigation targets and the approved escalation copy
 * (SWC-18869 Section 12). Lives in entities/WebMcp because it is keyed by
 * NavTarget + role — the agent's per-role prompt fragment lives separately in
 * features/AiAgent (roleContext) to respect FSD layering.
 */
export type GatedAction =
  | 'compliance'
  | 'billing'
  | 'members'
  | 'number_provisioning'
  | 'inbox_settings'
  | 'import'
  | 'analytics'

// Org-role seniority, low → high. Used to decide whether a role meets a catalog
// item's minimum (onboarding steps, discovery items).
const ROLE_RANK: Record<EnumUserRoles, number> = {
  [EnumUserRoles.VIEW_ONLY]: 0,
  [EnumUserRoles.MEMBER]: 1,
  [EnumUserRoles.MANAGER]: 2,
  [EnumUserRoles.ADMIN]: 3,
  [EnumUserRoles.OWNER]: 4,
}

export const roleAtLeast = (role: EnumUserRoles, min: EnumUserRoles): boolean =>
  ROLE_RANK[role] >= ROLE_RANK[min]

const OBSERVER_READONLY =
  'Your account is read-only. Contact your admin to update your role if you need to take this action.'

const MEMBER_MESSAGES: Partial<Record<GatedAction, string>> = {
  compliance:
    "Compliance registration is managed by admins. Your account doesn't have access to this area. Ask your admin to check the registration status or submit on your behalf.",
  import:
    'Importing contacts requires admin or manager access. Ask your admin to run the import, or they can grant you data management access in Settings → Roles & Permissions.',
  number_provisioning:
    "Purchasing phone numbers and creating inboxes requires admin access. I can't do that from your account. Ask your admin — once the inbox is ready, you'll be able to send messages from it straight away.",
  inbox_settings:
    'Changing inbox settings (including enabling voice or calling) requires admin or manager access. Ask your admin or manager to adjust the inbox for you.',
  analytics:
    'Analytics access is available to admins and managers. Your account is set to member. Ask your admin to pull the report, or ask them to update your role in Settings → Roles & Permissions.',
  members:
    'Inviting team members requires admin access. Ask your admin to send the invitation from Settings → Team Members.',
}

const MANAGER_MESSAGES: Partial<Record<GatedAction, string>> = {
  compliance:
    'Compliance registration requires owner or admin access. Contact your account owner or admin to manage this.',
  billing:
    "Billing management requires owner or admin access. You have manager access, which doesn't include billing. Contact your account owner or admin.",
  members: 'Inviting and managing team members requires admin access. Contact your account admin.',
}

const MEMBER_FALLBACK =
  'This requires admin or manager access. Contact your admin to take this action.'
const MANAGER_FALLBACK = 'This requires owner or admin access. Contact your account owner or admin.'

export const escalationMessage = (role: EnumUserRoles, action: GatedAction): string => {
  if (role === EnumUserRoles.VIEW_ONLY) return OBSERVER_READONLY
  if (role === EnumUserRoles.MANAGER) return MANAGER_MESSAGES[action] ?? MANAGER_FALLBACK
  if (role === EnumUserRoles.MEMBER) return MEMBER_MESSAGES[action] ?? MEMBER_FALLBACK
  // Owner/admin are never gated; a benign fallback keeps the signature total.
  return ''
}

// The compliance area and every screen inside it are gated together: the page's
// own access layout redirects a non-admin off any of them, so withholding the
// root while allowing a deep link would just bounce the user.
const COMPLIANCE_TARGETS = [
  'Compliance',
  'Business Profile',
  '10DLC Registration',
  'Toll-Free Registration',
] as const satisfies ReadonlyArray<NavTarget>

// Navigate destinations withheld per role (FR-014 + the member/observer onboarding
// gates in T031). Owner/admin have none.
const WITHHELD_TARGETS: Record<EnumUserRoles, ReadonlySet<NavTarget>> = {
  [EnumUserRoles.OWNER]: new Set(),
  [EnumUserRoles.ADMIN]: new Set(),
  [EnumUserRoles.MANAGER]: new Set<NavTarget>(COMPLIANCE_TARGETS),
  [EnumUserRoles.MEMBER]: new Set<NavTarget>([
    ...COMPLIANCE_TARGETS,
    'Import',
    'Number Provisioning',
    'Inbox Settings',
    'Analytics',
  ]),
  [EnumUserRoles.VIEW_ONLY]: new Set<NavTarget>([
    ...COMPLIANCE_TARGETS,
    'Import',
    'Number Provisioning',
    'Inbox Settings',
    'Analytics',
  ]),
}

const TARGET_ACTION: Partial<Record<NavTarget, GatedAction>> = {
  Compliance: 'compliance',
  'Business Profile': 'compliance',
  '10DLC Registration': 'compliance',
  'Toll-Free Registration': 'compliance',
  Import: 'import',
  'Number Provisioning': 'number_provisioning',
  'Inbox Settings': 'inbox_settings',
  Analytics: 'analytics',
}

/**
 * If `target` is withheld for `role`, returns the escalation message to show
 * instead of navigating; otherwise null (navigation may proceed).
 */
export const navGate = (role: EnumUserRoles, target: NavTarget): string | null => {
  if (!WITHHELD_TARGETS[role].has(target)) return null
  const action = TARGET_ACTION[target]
  return action ? escalationMessage(role, action) : null
}
